import * as L from 'leaflet';

const turf = require('@turf/turf');

export interface LeafletTaskStripState {
  available: boolean;
  enabled: boolean;
  lengthKm: number | null;
}

export interface LeafletTaskStripPreview {
  polygonLatLngs: L.LatLng[];
  lengthKm: number;
}

export const LEAFLET_TASK_STRIP_SWATH_KM = 8;

const POINT_REGEX = /POINT \(([-\d.]+) ([-\d.]+)\)/;

export function createLeafletTaskStripState(): LeafletTaskStripState {
  return {
    available: false,
    enabled: false,
    lengthKm: null,
  };
}

export function isLeafletTaskStripAvailable(points: string[]): boolean {
  return points.length === 2;
}

export function buildLeafletTaskStripPreview(
  points: string[],
  swathKm = LEAFLET_TASK_STRIP_SWATH_KM
): LeafletTaskStripPreview | null {
  if (!isLeafletTaskStripAvailable(points)) {
    return null;
  }

  const start = parsePointWkt(points[0]);
  const end = parsePointWkt(points[1]);

  if (!start || !end) {
    return null;
  }

  const startLatLng = L.latLng(start.lat, start.lng);
  const endLatLng = L.latLng(end.lat, end.lng);
  const lineLengthKm = startLatLng.distanceTo(endLatLng) / 1000;

  if (!lineLengthKm) {
    return null;
  }

  const halfSwathKm = swathKm / 2;
  const startPoint = turf.point([start.lng, start.lat]);
  const endPoint = turf.point([end.lng, end.lat]);
  const centerBearing = turf.bearing(startPoint, endPoint);

  const startLeft = turf.destination(startPoint, halfSwathKm, centerBearing - 90, {
    units: 'kilometers',
  });
  const endLeft = turf.destination(endPoint, halfSwathKm, centerBearing - 90, {
    units: 'kilometers',
  });
  const endRight = turf.destination(endPoint, halfSwathKm, centerBearing + 90, {
    units: 'kilometers',
  });
  const startRight = turf.destination(startPoint, halfSwathKm, centerBearing + 90, {
    units: 'kilometers',
  });

  const polygonLatLngs = [
    startLeft.geometry.coordinates,
    endLeft.geometry.coordinates,
    endRight.geometry.coordinates,
    startRight.geometry.coordinates,
    startLeft.geometry.coordinates,
  ].map(([lng, lat]) => L.latLng(lat, lng));

  return {
    polygonLatLngs,
    lengthKm: lineLengthKm,
  };
}

function parsePointWkt(pointWkt: string): { lng: number; lat: number } | null {
  const match = POINT_REGEX.exec(pointWkt);
  if (!match) {
    return null;
  }

  const lng = Number(match[1]);
  const lat = Number(match[2]);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return { lng, lat };
}